'use client';
import { useId } from 'react';

import { useControlledState } from '@/hooks/useControlledState';
import { cn } from '@/lib/utils';

import { HTMLDataTrackerProps } from '@/app/types';
import Button from '@/components/atoms/Button';
import { type ButtonElement } from '@/components/atoms/Button/ButtonContent';
import { gradientDecodoBorderStyling } from '@/styles/gradient';

import { pullDataTrackers } from '../../molecules/imageLink/utils';
import BadgeStrapi from '../badge/BadgeStrapi';

export interface ToggleProps {
  labelBadge?: string[];
  options: string[];
  selectedIndex?: number;
  onValueChange?: (index: number) => void;
  variant?: 'white' | 'gradient' | 'cubes';
  size?: 's' | 'm';
  responsive?: boolean;
  classNames?: {
    root?: string;
    container?: string;
    toggleItem?: ToggleItemProps['classNames'];
  };
}

export const Toggle = ({
  labelBadge,
  selectedIndex: propSelectedIndex,
  onValueChange,
  options,
  size = 'm',
  responsive = false,
  variant = 'white',
  classNames,
}: ToggleProps) => {
  const [selectedIndex, setSelectedIndex] = useControlledState({
    prop: propSelectedIndex ?? undefined,
    defaultProp: propSelectedIndex ?? 0,
    onChange: onValueChange,
  });
  const id = useId();

  const rootClasses = cn(
    'flex rounded-3xl p-px gap-x-[2px] gap-y-5 border border-solid border-gray-300 bg-base-white dark:bg-gray-dark-950',
    responsive &&
      'flex-col md:flex-row border-none md:border-solid md:border md:hover:bg-gray-50',
    !responsive && 'flex-row hover:bg-gray-50',
    variant === 'cubes' &&
      'p-2 rounded-[6px] bg-gray-100 border-none gap-x-[9px]'
  );

  return (
    <div className={cn('w-full md:w-auto', classNames?.container)}>
      <ul
        role='tablist'
        aria-orientation='horizontal'
        dir='ltr'
        className={cn(rootClasses, classNames?.root)}
      >
        {options.map((option, i) => (
          <ToggleItem
            key={option}
            value={option}
            isSelected={i === selectedIndex}
            onClick={() => {
              setSelectedIndex(i);
            }}
            onKeyDown={(event) => {
              if ([' ', 'Enter'].includes(event.key)) setSelectedIndex(i);
            }}
            onFocus={() => {
              if (selectedIndex !== i) {
                setSelectedIndex(i);
              }
            }}
            size={size}
            responsive={responsive}
            variant={variant}
            data-tracker-context={`toggle-index-${i}`}
            uniqueId={id}
            classNames={classNames?.toggleItem}
            labelBadge={labelBadge?.[i]}
          />
        ))}
      </ul>
    </div>
  );
};

interface ToggleItemProps extends HTMLDataTrackerProps {
  value: string;
  labelBadge?: string;
  isSelected: boolean;
  uniqueId: string;
  onClick: ButtonElement['onClick'];
  onKeyDown: ButtonElement['onKeyDown'];
  onFocus: ButtonElement['onFocus'];
  size: ToggleProps['size'];
  responsive: ToggleProps['responsive'];
  variant: ToggleProps['variant'];
  classNames?: {
    inner?: string;
    button?: string;
    selectedButton?: string;
    selectedPill?: string;
  };
}

const ToggleItem = ({
  value,
  labelBadge,
  onClick,
  onKeyDown,
  onFocus,
  isSelected,
  size,
  responsive,
  variant,
  classNames,
  ...dataTrackerProps
}: ToggleItemProps) => {
  return (
    <li
      className={cn(
        'group relative isolate w-full rounded-3xl text-center transition-colors duration-0',
        responsive &&
          'bg-base-white border border-gray-300 md:border-none md:bg-transparent',
        !responsive && 'border-none bg-transparent',
        !isSelected && 'hover:bg-gray-50 md:hover:bg-transparent',
        'flex items-center gap-2 justify-center',
        classNames?.inner,
        size === 'm' && 'text-button-text-m px-2 py-2 sm:px-6',
        size === 's' && 'text-button-text-s px-2 py-1 leading-5 sm:px-5'
      )}
    >
      <Button
        type='button'
        role='tab'
        aria-selected={isSelected}
        data-disabled={undefined}
        disabled={undefined}
        onClick={onClick}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        noStyles
        className={cn(
          'group-hover flex w-full items-center justify-center gap-2 text-nowrap font-serif',
          isSelected && 'cursor-default',
          isSelected && variant === 'gradient' && 'text-gray-950',
          isSelected && variant === 'white' && 'text-base-white',
          isSelected && variant === 'cubes' && 'text-gray-950',
          !isSelected &&
            variant === 'cubes' &&
            'rounded text-gray-500 transition-all duration-0 group-hover:bg-gray-200 group-hover:text-gray-950',
          classNames?.button,
          isSelected && classNames?.selectedButton
        )}
        {...pullDataTrackers(dataTrackerProps)}
      >
        {value}
        {labelBadge && (
          <BadgeStrapi
            block={{ text: labelBadge }}
            inner={{
              color: 'success',
              variant: 'soft',
              size: 'm',
              classNames: {
                root: 'group-hover:text-success-700 dark:group-hover:text-success-300',
              },
            }}
          />
        )}
      </Button>

      {isSelected && (
        <div
          aria-hidden
          className={cn(
            'absolute left-0 top-0 z-[-1] h-full w-full rounded-[20px]',
            variant === 'gradient' &&
              `bg-base-white
              ${gradientDecodoBorderStyling({
                borderWidth: 'p-0.5',
                color: 'gradient-main',
                borderRadius: 'rounded-3xl',
              })}`,
            variant === 'white' && 'bg-gray-950',
            variant === 'cubes' && 'shadow-card-d bg-base-white rounded',
            classNames?.selectedPill
          )}
        />
      )}
    </li>
  );
};
